import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { login, getUserInfoByTempToken } from '../services/authServices';
import { useSiteContext } from '../context/SiteContext';

const useLoginSubmit = () => {
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const { setToken, setIsAuthenticated, setUser } = useSiteContext();
    
    useEffect(() => {
        // Note: get query params from URL to determine if editing or creating
        const queryParams = new URLSearchParams(window.location.search);
        const tempTokenFromUrl = queryParams.get('temp_token');
        if (tempTokenFromUrl) {
            autoLoginWithTempToken(tempTokenFromUrl);
        }
    }, []);

    const {
        register,
        handleSubmit,
        formState: { errors },
        setError,
        setValue,
        clearErrors
    } = useForm({
        defaultValues: {
            email: '',
            password: '',
            rememberMe: false
        },
        mode: 'onSubmit'
    });

    const onSubmit = async (data) => {
        setLoading(true);
        clearErrors();

        try {
            const response = await login({
                email: data.email,
                password: data.password,
                rememberMe: data.rememberMe
            });
            console.log("response", response);
            if (response.success && response.token) {
                if (response.redirectUrl) {
                    try {
                        const target = new URL(response.redirectUrl);
                        if (target.host !== window.location.host) {
                            toast.success('Redirecting to your workspace...');
                            window.location.href = response.redirectUrl;
                            return;
                        }
                    } catch (e) {
                        // fall through to normal login if URL parsing fails
                    }
                }
                localStorage.setItem('token', response.token);
                localStorage.setItem('user', JSON.stringify(response.user));
                setToken(response.token);
                setIsAuthenticated(true);
                setUser(response.user);
                toast.success('Login successful!');

                // Honor ?redirect= so deep-links (e.g. /invite/:token) survive
                // the login round-trip. Restrict to same-origin relative paths
                // ('/foo' but not '//evil.com') to avoid open-redirect.
                const redirectParam = new URLSearchParams(window.location.search).get('redirect');
                const safeRedirect =
                    redirectParam && redirectParam.startsWith('/') && !redirectParam.startsWith('//')
                        ? redirectParam
                        : null;
                if (safeRedirect) {
                    // Invite acceptance pages use ?autoAccept=1 to fire the
                    // accept call on arrival; otherwise the user has to click
                    // a button they never saw before login.
                    let target = safeRedirect;
                    if (target.startsWith('/invite/')) {
                        const sep = target.includes('?') ? '&' : '?';
                        target = `${target}${sep}autoAccept=1`;
                    }
                    navigate(target);
                } else if (localStorage.getItem('roomId')) {
                    navigate(`/${localStorage.getItem('roomId')}`);
                } else {
                    navigate('/dashboard');
                }
            } else {
                // throw new Error('No token received');
                setError('email', {
                    type: 'manual',
                    message: 'Invalid email or password'
                });
            }

        } catch (error) {
            console.log("error", error);
            const errorMessage = error.response?.data?.message || 'Login failed. Please try again.';
            // Set form error for general submission
            setError('email', {
                type: 'manual',
                message: errorMessage
            });
        } finally {
            setLoading(false);
        }
    };

    const autoLoginWithTempToken = async (tempToken) => {
        setLoading(true);
        try {
            // Note: Get user info using the temp token
            const userInfo = await getUserInfoByTempToken(tempToken);
            if (userInfo && userInfo.data) {
                const response = await login({
                    email: userInfo.data.email,
                    password: 'xxxxxxxx',
                    auto_login: true,
                });
                if (response.success && response.token) {
                    localStorage.setItem('token', response.token);
                    localStorage.setItem('user', JSON.stringify(response.user));
                    setToken(response.token);
                    setIsAuthenticated(true);
                    setUser(response.user);
                    toast.success('Login successful!');
                    if (localStorage.getItem('roomId')) {
                        navigate(`/${localStorage.getItem('roomId')}`);
                    } else {
                        navigate('/dashboard');
                    }
                } else {
                    // throw new Error('No token received');
                    setError('email', {
                        type: 'manual',
                        message: 'Invalid email or password'
                    });
                }
            } else {
                toast.error(userInfo?.message || "Auto-login failed. Please login manually.");
            }
        } catch (error) {
            console.log("auto login error", error);
            toast.error('Auto login failed. Please login manually.');
        } finally {
            setLoading(false);
        }
    };

    return {
        register,
        handleSubmit: handleSubmit(onSubmit),
        errors,
        loading,
        onSubmit,
        setValue
    };
};

export default useLoginSubmit;
